/** App-owned IndexedDB database holding ONLY local scan pages (offline cache). */
export const SCAN_GRADING_DB_NAME = 'dg_scan_grading_v1';
/** The single object store inside {@link SCAN_GRADING_DB_NAME}. */
export const SCAN_GRADING_STORE_NAME = 'pages';
const DB_NAME = SCAN_GRADING_DB_NAME;
const STORE = SCAN_GRADING_STORE_NAME;
const DB_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
  });
}

export type StoredScanPage = {
  id: string;
  sessionId: string;
  base64Image: string;
  thumbUrl?: string;
  createdAt: number;
  uploaded: boolean;
};

export async function savePageLocal(page: StoredScanPage): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(page);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function listPagesForSession(sessionId: string): Promise<StoredScanPage[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => {
      const all = (req.result as StoredScanPage[]).filter((p) => p.sessionId === sessionId);
      resolve(all.sort((a, b) => a.createdAt - b.createdAt));
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Deletes the entire app-owned scan-pages IndexedDB database. Used by the
 * Privacy 6C "clear local browser data" flow. This DB holds ONLY locally-cached
 * scan page bytes (the server is the source of truth once a page uploads), so
 * deleting it removes local image cache without touching server data.
 *
 * Resolves `true` when the database was deleted (or did not exist), `false`
 * when the delete was blocked/errored. Never throws; storage may be unavailable
 * (SSR, private mode, security errors).
 */
export function deleteScanGradingDatabase(): Promise<boolean> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(false);
  return new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = () => resolve(true);
      req.onerror = () => resolve(false);
      // `blocked` fires if another tab holds an open connection; treat as not
      // fully cleared so the caller can report a partial result honestly.
      req.onblocked = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

export async function markPageUploaded(id: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const row = getReq.result as StoredScanPage | undefined;
      if (row) {
        row.uploaded = true;
        store.put(row);
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
